/**
 * Money is always an integer number of pence (ADR-0004). Percentages are
 * integer basis points (2000 = 20%). Nothing here uses floating-point maths on
 * currency: user input is parsed as text.
 */
export type Pence = number & { readonly __brand: 'Pence' }

export function pence(n: number): Pence {
  if (!Number.isSafeInteger(n)) throw new RangeError(`Not integer pence: ${n}`)
  return n as Pence
}

const MONEY = /^£?\s*(\d{1,3}(?:,\d{3})+|\d+)(?:\.(\d{1,2}))?$/

/** Parse user-entered pounds ("£1,132.50", "706.5") into pence, or null. */
export function parsePence(input: string): Pence | null {
  const m = MONEY.exec(input.trim())
  if (!m) return null
  const pounds = m[1]!.replaceAll(',', '')
  const frac = (m[2] ?? '').padEnd(2, '0')
  const value = Number(pounds) * 100 + Number(frac)
  return Number.isSafeInteger(value) ? pence(value) : null
}

const GBP = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' })

export function formatPence(p: number): string {
  return GBP.format(p / 100)
}

/** Pounds as a plain editable string ("706.50"), for form fields. */
export function penceToInput(p: number): string {
  const sign = p < 0 ? '-' : ''
  const abs = Math.abs(p)
  return `${sign}${Math.trunc(abs / 100)}.${String(abs % 100).padStart(2, '0')}`
}

/** VAT on a net amount in basis points, rounded half-up to the penny. */
export function vatOn(net: Pence, rateBp: number): Pence {
  return pence(Math.floor((net * rateBp + 5_000) / 10_000))
}

const PERCENT = /^(\d{1,3})(?:\.(\d{1,2}))?\s*%?$/

/** Parse "20", "17.5%" into basis points (0–10000), or null. */
export function percentToBp(input: string): number | null {
  const m = PERCENT.exec(input.trim())
  if (!m) return null
  const bp = Number(m[1]) * 100 + Number((m[2] ?? '').padEnd(2, '0'))
  return bp <= 10_000 ? bp : null
}

export function formatPercentBp(bp: number): string {
  const whole = Math.trunc(bp / 100)
  const frac = Math.abs(bp % 100)
  if (frac === 0) return `${whole}%`
  return `${whole}.${String(frac).padStart(2, '0').replace(/0$/, '')}%`
}

/** Gross margin (sell − cost) / sell in basis points, truncated; null when sell is 0. */
export function marginBp(sell: Pence, cost: Pence): number | null {
  if (sell === 0) return null
  return Math.trunc(((sell - cost) * 10_000) / sell)
}
